import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { noop } from 'lodash-es'
import { ResizeObserver } from 'resize-observer'
import { css } from 'src/lib/emotion'

import { colors } from '../../tailwind'
import { classes } from '../../utils/css'

const editableBase = css`
  position: relative;

  &[data-show-icon] {
    &:after {
      content: '\\200B';
      width: 1em;
      height: auto;
      line-height: inherit;
      display: inline-block;
      position: absolute;
      top: 0;
      right: 0;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 16 16"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/></svg>');
      background-position: center center;
      background-repeat: no-repeat;
      opacity: 0;
    }

    &:hover,
    &:focus {
      padding-right: 25px;
      &:after {
        opacity: 0.5;
      }
    }
  }

  &:empty {
    &:focus:before {
      content: attr(data-placeholder);
    }
    &:before {
      position: relative;
      opacity: 1;
      content: attr(data-active-placeholder);
      font-style: italic !important;
      font-weight: normal !important;
    }
  }

  &:focus {
    &:before {
      opacity: 1;
      z-index: -1;
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
      background: linear-gradient(to bottom, black, ${colors['surface-base']});
      margin: -0.3em -0.4em;
      padding: 0.3em 0.4em;
    }
  }

  &[data-show-focus]:focus {
    box-shadow: 0 0 20px 20px rgba(0, 0, 0, 0.5);
    &:before {
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) inset, 0 0 0 2px ${colors.link};
    }
  }

  &:before {
    content: '';
    display: block;
    pointer-events: none;
    opacity: 0;
    transition: 0.1s linear;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0.3em;
    color: rgba(255, 255, 255, 0.3);
  }
`
let spacer: HTMLSpanElement
let previousValue: string
let resizeObserver: ResizeObserver
let focused: HTMLSpanElement

document.body.addEventListener(
  'scroll',
  (e) => {
    if (focused && (e.target as HTMLElement).contains(focused)) {
      focused.blur()
    }
  },
  { capture: true, passive: true }
)
window.addEventListener('resize', () => focused && focused.blur(), { capture: true, passive: true })

export const EditableText: FunctionalComponent<
  {
    disabled?: boolean
    allowWrap?: boolean
    hideIcon?: boolean
    hideFocus?: boolean
    allowEmpty?: boolean
    autoFocus?: boolean
    value?: string
    placeholder?: string
    activePlaceholder?: string
    activeClass?: string
    hoverClass?: string
  } & JSXBase.IntrinsicElements['span']
> = ({
  disabled = false,
  autoFocus = false,
  allowWrap = false,
  hideIcon = false,
  hideFocus = false,
  allowEmpty = false,
  placeholder,
  activePlaceholder,
  value = '',
  class: className = '',
  style,
  activeClass = '',
  hoverClass = '',
  onFocus = noop,
  onBlur = noop,
  onChange = noop,
  ...attrs
}) => {
  const activeClasses = activeClass && activeClass.split(' ')
  const hoverClasses = hoverClass && hoverClass.split(' ')
  return (
    <span
      class={`
        ${className}
        ${classes(!disabled, editableBase, '')}
        ${classes(!allowWrap, 'break-all truncate')}
      `}
      style={style}
      contentEditable={!disabled}
      innerHTML={value}
      data-placeholder={placeholder || 'Enter text…'}
      data-active-placeholder={activePlaceholder || placeholder || 'Enter text…'}
      data-show-icon={!hideIcon}
      data-show-focus={!hideFocus}
      {...attrs}
      ref={(el) => {
        if (el && autoFocus && el !== focused) {
          setTimeout(() => el.focus())
          focused = el
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          const input = e.target as HTMLElement
          input.blur()
          e.preventDefault()
        }
      }}
      onMouseEnter={(event) => {
        const el = event.target as HTMLSpanElement
        hoverClasses && el.classList.add(...hoverClass)
      }}
      onMouseLeave={(event) => {
        const el = event.target as HTMLSpanElement
        hoverClasses && el.classList.remove(...hoverClass)
      }}
      onChange={onChange}
      onFocus={(event) => {
        const el = event.target as HTMLSpanElement
        focused = el
        if (resizeObserver) {
          resizeObserver.disconnect()
        }
        resizeObserver = new ResizeObserver(() => {
          const rect = spacer.getBoundingClientRect()
          el.classList.add('z-menu')
          el.style.width = rect.width + 'px'
          el.style.top = rect.y + 'px'
          el.style.left = rect.x + 'px'
        })
        previousValue = el.textContent
        activeClasses && el.classList.add(...activeClasses)
        spacer = el.cloneNode(true) as HTMLSpanElement
        spacer.contentEditable = 'false'
        spacer.style.width = '100%'
        spacer.style.visibility = 'hidden'
        Object.keys(attrs).map((attr) => spacer.removeAttribute(attr))
        el.style.position = 'fixed'
        resizeObserver.observe(spacer)
        el.classList.remove('truncate')
        el.parentElement.insertBefore(spacer, el)
        onFocus(event)
      }}
      onInput={(event) => {
        const el = event.target as HTMLSpanElement
        if (allowWrap) {
          spacer.style.height = `${el.clientHeight}px`
        }
        if (!el.textContent) {
          // Webkit/Safari will sometimes leave a <br> which screws up the css
          // :empty scelector so this makes sure it's zeroed out
          el.innerHTML = ''
        }
      }}
      onBlur={(event) => {
        const el = event.target as HTMLSpanElement
        if (focused === el) focused = null
        resizeObserver.disconnect()
        if (!el.textContent && !allowEmpty) {
          el.textContent = previousValue
        }
        activeClasses && el.classList.remove(...activeClasses)
        !allowWrap && el.classList.add('truncate')
        el.classList.remove('z-menu')
        el.scrollTo(0, 0)
        el.style.transition = null
        el.style.position = null
        el.style.top = null
        el.style.left = null
        el.style.width = null
        spacer.remove()
        if (el.textContent !== previousValue) {
          el.dispatchEvent(new Event('change'))
        }
        onBlur(event)
      }}
    />
  )
}
